import EventEmitter from 'eventemitter3';
import { MenuItemCartCardSingleMode } from './menu-item-cart-card-single-mode';
import { MenuItemCartCardMultiMode } from './menu-item-cart-card-multi-mode';
import { IMenuItemCartCardUiParams, MenuItemCartCardBase } from './menu-item-cart-card-base';
import { Data } from '../../../models/data';
import { EventTypes } from '../../../core/event-bus';
import { CustomizeItemUiOption } from '../../../types/enums';

/**
 * Acts as the main controller for the item customization flow.
 * Manages shared dependencies, event subscriptions, core logic, and UI rendering mode (single vs multi).
 */
export class MenuItemCartCardController {
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;

    private readonly _uiSingle: MenuItemCartCardSingleMode;
    private readonly _uiMulti: MenuItemCartCardMultiMode;
    private _activeUi: MenuItemCartCardBase | null = null;

    constructor(params: IMenuItemCartCardUiParams) {
        this._eventBus = params.eventBus;
        this._data = params.data;

        this._uiSingle = new MenuItemCartCardSingleMode(params);
        this._uiMulti = new MenuItemCartCardMultiMode(params);

        this._activeUi = null;
        this._eventBusListeners();
    }

    /**
     * Gets the current UI instance (single or multi).
     * @returns {MenuItemCartCardBase} The UI controller instance.
     */
    get ui(): MenuItemCartCardBase | null {
        return this._activeUi;
    }

    /**
     * Registers listeners to respond to application-wide events.
     * Currently listens for store changes to potentially update the UI mode.
     */
    protected _eventBusListeners(): void {
        this._eventBus.on(EventTypes.DATA_RETRIEVED, this._changeUiView);
        this._eventBus.on(EventTypes.LANGUAGE_CHANGED, () => {
            if (this._activeUi)
                this._activeUi.render();
        });
    }

    /**
     * Changes the UI view mode (single or multi) when store-related data changes.
     * Currently stubbed - uncomment logic when dynamic switching is supported.
     */
    private readonly _changeUiView = (): void => {
        const isSingle = this._data.getProperty("Settings").OOS.customizeItemMode === CustomizeItemUiOption.SINGLE_ITEM;
        this._activeUi = isSingle ? this._uiSingle : this._uiMulti;
    }
}